import React, { useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { useTheme } from '../../../../../contexts/ThemeContext';
import { getPaletteEntry } from '../../../statusPalette';
import { useNodeDimensions } from '../../hooks/useNodeDimensions';
import { useTabFocalTrigger } from '../../hooks/useTabFocalTrigger';
import { toolAriaLabel } from '../ariaLabels';
import type { ToolCallDagData } from '../../types';

// v0.4 Task 7: rich tool_call card.
//
//   Kind identity: 🔧 header glyph.
//   Status identity: getPaletteEntry(status) drives whole-card tone
//     (border + bg tint + label). Running → violet, error → rose,
//     cached → indigo, rate_limited/timeout → amber, ok → emerald.
//     One palette source — no more "amber=tool" collisions.
//
// wrapperRef lives here so Task 10 can wire useNodeDimensions.

function fmtBytes(n?: number | null): string | null {
  if (n == null) return null;
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
function fmtDuration(ms?: number | null): string | null {
  if (ms == null) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;
}

export interface ToolCallDagNodeRichProps {
  id: string;
  data: ToolCallDagData;
}

export const ToolCallDagNodeRich: React.FC<ToolCallDagNodeRichProps> = ({
  id,
  data,
}) => {
  const { darkMode } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  useNodeDimensions(id, wrapperRef);
  const tabTrigger = useTabFocalTrigger(data.turnKey);

  const call = data.call;
  const toolName =
    call.identity?.tool_name ?? (call as any).tool_name ?? 'tool';
  const status = call.outcome?.status ?? 'ok';
  const palette = getPaletteEntry(status);
  const duration = fmtDuration(call.timing?.duration_ms);
  const argsBytes = fmtBytes((call as any).args_bytes);
  const resultBytes = fmtBytes((call as any).result_bytes);
  const isError = status !== 'ok' && status !== 'cached';
  const errMsg = call.outcome?.error_message;
  const args = call.arguments ?? {};
  const result: any = (call as any).result ?? {};

  return (
    <div
      ref={wrapperRef}
      role="button"
      tabIndex={0}
      aria-label={toolAriaLabel(data)}
      onFocus={tabTrigger.onFocus}
      onBlur={tabTrigger.onBlur}
      className={`w-[320px] rounded-lg shadow-md border focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 focus-visible:ring-offset-2 ${palette.bgClass}`}
      data-node-id={id}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="l"
        className="!opacity-0 !pointer-events-none"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="r"
        className="!opacity-0 !pointer-events-none"
      />
      <div
        className={`p-3 border-b ${
          darkMode ? 'border-gray-700' : 'border-gray-300'
        }`}
      >
        <div
          className={`font-mono font-bold text-sm ${
            darkMode ? 'text-gray-100' : 'text-gray-900'
          } flex items-center gap-2`}
        >
          <span>🔧</span>
          <span className="truncate">{toolName}</span>
          <span className={`ml-auto ${palette.color}`}>{palette.icon}</span>
        </div>
        <div
          className={`text-[10px] mt-0.5 flex gap-2 flex-wrap ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          <span>round {data.round}</span>
          <span>·</span>
          <span className={palette.color}>{palette.label ?? status}</span>
          {duration && (
            <>
              <span>·</span>
              <span>{duration}</span>
            </>
          )}
          {argsBytes && (
            <>
              <span>·</span>
              <span>args {argsBytes}</span>
            </>
          )}
          {resultBytes && (
            <>
              <span>·</span>
              <span>→ {resultBytes}</span>
            </>
          )}
        </div>
      </div>
      <div className="p-3 text-[11px]">
        <div
          className={`text-[9px] uppercase tracking-widest mb-1 ${
            darkMode ? 'text-gray-500' : 'text-gray-500'
          }`}
        >
          Arguments
        </div>
        <pre
          className={`font-mono whitespace-pre-wrap break-words ${
            darkMode ? 'text-gray-300' : 'text-gray-800'
          }`}
          style={{ maxHeight: '160px', overflowY: 'auto' }}
        >
          {JSON.stringify(args, null, 2).slice(0, 800)}
        </pre>
        {isError ? (
          <div
            className={`mt-2 p-2 rounded border ${palette.bgClass} ${palette.color}`}
          >
            <div className="font-semibold text-[10px] uppercase mb-1 flex items-center gap-1">
              <span>{palette.icon}</span> {palette.label ?? 'error'}
            </div>
            <div>{errMsg ?? 'tool failed'}</div>
          </div>
        ) : (
          <>
            <div
              className={`text-[9px] uppercase tracking-widest mb-1 mt-2 ${
                darkMode ? 'text-gray-500' : 'text-gray-500'
              }`}
            >
              Result
            </div>
            <div
              className={`font-mono ${
                darkMode ? 'text-emerald-300' : 'text-emerald-700'
              }`}
            >
              ✓ success
              {typeof result?.count === 'number' ? ` · ${result.count} items` : ''}
              {typeof result?.document_id === 'string'
                ? ` · ${result.document_id}`
                : ''}
              {typeof result?.peer_count === 'number'
                ? ` · ${result.peer_count} peers (${result.ok_count ?? 0} ok)`
                : ''}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
